import React, { useState } from "react";
import _isEmpty from "lodash/isEmpty";
import { Modal, Notification } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../models";
import { setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { getActiveSubscription } from "../../../checkout/state/selectors/cartSelectors";
import { SubscriptionDropdown } from "../../components/molecules/SubscriptionDropdown";
import { EquipmentForm } from "../../components/organisms/EquipmentForm";
import { addExternalHardware, fetchProductByEan } from "../../state/actions/hardwareActions";
import "./AddEquipmentModal.less";

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
}

const AddEquipmentModal: React.FC<Props> = ({ isModalOpen, setIsModalOpen }) => {
    const dispatch = useDispatch();
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const [message, setMessage] = useState<string | null>(null);
    const [notificationVariant, setNotificationVariant] = useState<"error" | "warning" | "information">("error");

    const handleLookupEan = async (ean: string) => {
        try {
            setMessage(null);
            const product = await dispatch(fetchProductByEan(ean));

            if (_isEmpty(product)) {
                setNotificationVariant("information");
                setMessage(
                    `Kunne ikke finne produkt med EAN ${ean}. Dobbeltsjekk at EAN er riktig og prøv igjen, eller huk av "Legg til modell og pris manuelt" for å registrere manuelt.`,
                );
            }
        } catch (e: any) {
            setNotificationVariant("error");
            setMessage(e?.displayable ? e.error : "Kunne ikke hente produktinformasjon");
        }
    };

    const handleSubmit = async (selectedCommitmentDuration?: string, imei?: string) => {
        if (activeSubscription) {
            setMessage(null);

            const result = await dispatch(
                addExternalHardware(
                    activeSubscription,
                    selectedCommitmentDuration ? Number(selectedCommitmentDuration) : 0,
                    imei,
                ),
            );

            if (!result.success) {
                setNotificationVariant("error");
                setMessage(result.message ?? "Kunne ikke legge til utstyr. Vennligst prøv igjen senere.");
                return;
            }

            setIsModalOpen(false);
        }
    };

    const handleSubscriptionChange = (subscriptionId: string) => {
        dispatch(setActiveSubscription(subscriptionId));
    };

    return (
        <>
            <Modal defaultOpen open={isModalOpen}>
                <Modal.Overlay className="add-equipment-modal">
                    <Modal.Content className="add-equipment-modal__content">
                        <Modal.Group variant="title" className="add-equipment-modal__title-group">
                            <Modal.Title as="h3">Velg telefon / Manuell registrering</Modal.Title>
                        </Modal.Group>
                        <Modal.Close
                            aria-label="lukk"
                            slot="floating"
                            onClick={() => setIsModalOpen(false)}
                            className="add-equipment-modal__close"
                        />
                        <div className="add-equipment-modal__subscription-dropdown">
                            <SubscriptionDropdown
                                selectedSubscriptionId={activeSubscription}
                                onSubscriptionChange={handleSubscriptionChange}
                                variant="equipment"
                            />
                        </div>
                        <Modal.Group direction="column">
                            {!!message && (
                                <Notification type="tile" variant={notificationVariant} mt="100">
                                    <Notification.Icon />
                                    <Notification.Text>{message}</Notification.Text>
                                </Notification>
                            )}
                            {activeSubscription && (
                                <EquipmentForm onSubmit={handleSubmit} onEanLookup={handleLookupEan} />
                            )}
                        </Modal.Group>
                    </Modal.Content>
                </Modal.Overlay>
            </Modal>
        </>
    );
};

export default AddEquipmentModal;
